import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Calendar, Send, Save, Sparkles, LogOut, ArrowLeft, Image, Edit3, Video, Mail, Clock, Users, Printer, Download, FileText, Loader2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { UserTemplateEditor } from '../components/UserTemplateEditor';
import { ReelVideoEditor } from '../components/ReelVideoEditor';
import { NewsletterBuilder } from '../components/NewsletterBuilder';
import { ScheduleSheet } from '../components/ScheduleSheet';
import { PublishDestinationPicker } from '../components/PublishDestinationPicker';
import { useActiveListing } from '../contexts/ActiveListingContext';

// Substitute {{placeholder}} tokens AND id-based property fields on a single
// canvas element using the given propertyData. Module-scope so we can reuse
// this when (a) loading a fresh template and (b) re-loading a saved draft on
// Edit — the draft may have been created before substitution ran, or its
// property_data may have been updated after the fact.
const substitutePropertyOnElement = (el, propertyData) => {
  if (!propertyData) return el;

  // QR code placeholder
  if (el.type === 'qrCodePlaceholder' && propertyData.listing_url) {
    return { ...el, qrUrl: propertyData.listing_url };
  }

  if (!el.text) return el;

  const elementId = el.id?.toLowerCase() || '';
  let newText = el.text;
  const hasPlaceholders = newText.includes('{{');

  const priceValue = propertyData.price?.toString().replace(/[^0-9]/g, '') || '';
  const formattedPrice = priceValue ? `$${parseInt(priceValue).toLocaleString()}` : '';
  const priceWithoutDollar = priceValue ? parseInt(priceValue).toLocaleString() : '';
  const bedsValue = propertyData.beds || '';
  const bathsValue = propertyData.baths || '';
  const sqftValue = propertyData.sqft?.toString().replace(/[^0-9]/g, '') || '';
  const addressValue = propertyData.address || '';
  const summaryValue = propertyData.property_summary || propertyData.description || '';
  const keyFeatures = propertyData.key_features || [];
  const keyFeaturesText = keyFeatures.map(f => `✓ ${f}`).join('\n');

  if (hasPlaceholders) {
    // Case-INSENSITIVE substitution so {{ADDRESS}}, {{Address}}, {{address}}
    // (and the same for PRICE/BEDS/etc.) all match. Templates rotate per
    // listing so we can't predict which casing a designer used.
    // Mirrors backend `_apply_property_to_canvas` mapping in
    // /app/backend/routes/scraper.py.
    const cityValue = propertyData.city
      || (addressValue.includes(',') ? addressValue.split(',').slice(1).join(',').trim() : '');
    const mapping = {
      '${{price}}': formattedPrice,
      '{{price}}': priceWithoutDollar,
      '{{address}}': addressValue,
      '{{beds}}': bedsValue,
      '{{baths}}': bathsValue,
      '{{sqft}}': sqftValue,
      '{{description}}': summaryValue,
      '{{summary}}': summaryValue,
      '{{property_type}}': String(propertyData.property_type || ''),
      '{{year_built}}': String(propertyData.year_built || ''),
      '{{key_features}}': keyFeaturesText,
      '{{features}}': keyFeaturesText,
      '{{city}}': cityValue,
      '{{neighborhood}}': String(propertyData.neighborhood || cityValue || ''),
      '{{agent_name}}': String(propertyData.agent_name || ''),
      '{{agent_phone}}': String(propertyData.agent_phone || ''),
      '{{agent_email}}': String(propertyData.agent_email || ''),
      '{{agent_title}}': String(propertyData.agent_title || ''),
      '{{agent_license}}': String(propertyData.agent_license || ''),
      '{{agent_website}}': String(propertyData.agent_website || ''),
      '{{brokerage}}': String(propertyData.brokerage || ''),
      '{{brokerage_address}}': String(propertyData.brokerage_address || ''),
    };
    const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Process longer keys first so '${{price}}' wins over '{{price}}'.
    const keys = Object.keys(mapping).sort((a, b) => b.length - a.length);
    for (const k of keys) {
      const val = mapping[k];
      if (!val) continue; // leave the raw token in place if no data
      const pattern = new RegExp(escapeRegex(k), 'gi');
      newText = newText.replace(pattern, val);
    }
    return { ...el, text: newText };
  }

  if (el.isPropertyField || elementId === 'price' || elementId === 'price-label' || elementId === 'price-text') {
    if (formattedPrice) return { ...el, text: formattedPrice };
  }
  if (elementId === 'address' || elementId === 'address-text' || elementId === 'address-line') {
    if (addressValue) return { ...el, text: addressValue };
  }
  if (elementId === 'city' || elementId === 'city-text' || elementId === 'location') {
    if (addressValue) {
      const parts = addressValue.split(',');
      if (parts.length > 1) return { ...el, text: parts.slice(1).join(',').trim() };
    }
  }
  if (elementId === 'details' || elementId === 'features' || elementId === 'features-text' || elementId === 'stats') {
    const detailParts = [];
    if (bedsValue) detailParts.push(`${bedsValue} Beds`);
    if (bathsValue) detailParts.push(`${bathsValue} Baths`);
    if (sqftValue) detailParts.push(`${parseInt(sqftValue).toLocaleString()} Sq Ft`);
    if (detailParts.length > 0) return { ...el, text: detailParts.join('  •  ') };
  }
  if ((elementId === 'key-features' || elementId === 'key_features' || elementId === 'bullet-features') && keyFeatures.length > 0) {
    return { ...el, text: keyFeaturesText };
  }
  if (elementId.includes('beds') && bedsValue) {
    if (newText.toLowerCase().includes('beds')) {
      return { ...el, text: newText.replace(/\d+/, bedsValue) };
    }
    return { ...el, text: `${bedsValue} Beds` };
  }
  if (elementId.includes('baths') && bathsValue) {
    if (newText.toLowerCase().includes('bath')) {
      return { ...el, text: newText.replace(/\d+/, bathsValue) };
    }
    return { ...el, text: `${bathsValue} Baths` };
  }
  if (elementId.includes('sqft') && sqftValue) {
    if (newText.toLowerCase().includes('sq')) {
      return { ...el, text: newText.replace(/[\d,]+/, parseInt(sqftValue).toLocaleString()) };
    }
    return { ...el, text: `${parseInt(sqftValue).toLocaleString()} Sq Ft` };
  }
  if ((elementId === 'description' || elementId === 'description-text' || elementId === 'summary') && summaryValue) {
    return { ...el, text: summaryValue };
  }
  return el;
};

// Apply hero image to the first empty photoPlaceholder or convert it to an
// image element. Used when re-loading a saved draft so that if the draft's
// canvas never received the hero photo (older draft, manual creation), the
// editor still shows the property's main image.
const applyHeroImageToElements = (elements, heroUrl) => {
  if (!heroUrl || !Array.isArray(elements) || elements.length === 0) return elements;
  // Bail if a photoPlaceholder has already been filled OR an image element
  // already carries the hero — nothing to do.
  const alreadyHasHero = elements.some(el =>
    (el.type === 'image' && el.src === heroUrl) ||
    (el.wasPhotoPlaceholder && el.src)
  );
  if (alreadyHasHero) return elements;
  let applied = false;
  return elements.map(el => {
    if (applied) return el;
    if (el.type === 'photoPlaceholder' && !el.src) {
      applied = true;
      return {
        ...el,
        type: 'image',
        src: heroUrl,
        editable: true,
        wasPhotoPlaceholder: true,
        imageZoom: el.imageZoom ?? 1,
        imageOffsetX: el.imageOffsetX ?? 0.5,
        imageOffsetY: el.imageOffsetY ?? 0.5,
      };
    }
    return el;
  });
};

export const CreateCampaignPage = () => {
  const { templateId } = useParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { savePhotoAdjustments, activeListing } = useActiveListing();

  // If we land here from a draft card, the URL carries ?campaign=<id> so we
  // update the existing draft instead of creating a duplicate on every save.
  const existingCampaignId = new URLSearchParams(location.search).get('campaign');
  
  // Get property data from Print Center if available
  const initialPropertyData = location.state?.propertyData || {};
  const isPrintTemplate = location.state?.isPrint || false;
  const fromMagicCreate = location.state?.fromMagicCreate || false;

  // When editing a saved draft, the draft's own property_data lives in
  // MongoDB and arrives via fetchExistingCampaign — stash it here so the
  // NewsletterBuilder (and other downstream consumers) receive it even
  // though no `location.state.propertyData` was passed on the Edit nav.
  const [draftPropertyData, setDraftPropertyData] = useState(null);
  // Auto-loaded from /api/profile/brand-kit so {{agent_*}} and {{brokerage}}
  // placeholders substitute correctly even on non-Magic-Import flows.
  const [agentBrandContext, setAgentBrandContext] = useState({});
  // Merge: explicit nav state wins (fresh Magic Create), draft data fills
  // gaps (Edit-from-campaign-card flow). Brand-kit agent fields are merged
  // underneath as a final fallback so scraped/draft values still win.
  const rawPropertyData = (initialPropertyData && Object.keys(initialPropertyData).length > 0)
    ? initialPropertyData
    : (draftPropertyData || {});
  const effectivePropertyData = { ...agentBrandContext, ...rawPropertyData };

  // Photos available for auto-filling image blocks and for the asset picker.
  // We union main_image_url + the photos array so that listings scraped with
  // only a single hero image still get a hero applied to the newsletter
  // (previously NewsletterBuilder bailed out of auto-fill when photos was [] ).
  const propertyPhotos = (() => {
    const arr = effectivePropertyData.photos || [];
    const hero = effectivePropertyData.main_image_url;
    if (hero && !arr.includes(hero)) return [hero, ...arr];
    return arr;
  })();
  const mainImageUrl = effectivePropertyData.main_image_url || propertyPhotos[0] || null;
  
  // Helper to check if template is a print type
  const isPrintType = (type) => ['flyer', 'postcard', 'brochure'].includes(type);
  
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('editor');
  
  // Campaign data
  const [caption, setCaption] = useState('');
  // Publishing destinations selected by the user via <PublishDestinationPicker />.
  // Each entry = { account_id, placement } where placement is 'feed' | 'story' | 'reel'.
  const [publishDestinations, setPublishDestinations] = useState([]);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [customElements, setCustomElements] = useState([]);
  const [customBgColor, setCustomBgColor] = useState('#FFFFFF');
  
  // Preview image (captured from canvas)
  const [previewImage, setPreviewImage] = useState(null);
  const editorRef = useRef(null);

  // Auto-save
  const [draftCampaignId, setDraftCampaignId] = useState(existingCampaignId || null);
  // Track if existing-campaign load is in flight so we can defer rendering the
  // UserTemplateEditor until we know whether to seed it with the draft's saved
  // canvas elements (otherwise the editor mounts with the template's raw
  // {{ADDRESS}} placeholders and never gets refreshed).
  const [draftHydrated, setDraftHydrated] = useState(!existingCampaignId);
  const [draftElements, setDraftElements] = useState(null);
  const [draftBgColor, setDraftBgColor] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // idle | saving | saved | error
  const autoSaveTimerRef = useRef(null);
  const elementsRef = useRef(customElements);
  const bgColorRef = useRef(customBgColor);
  elementsRef.current = customElements;
  bgColorRef.current = customBgColor;
  
  // Reel data
  const [reelData, setReelData] = useState(null);
  
  // Newsletter data
  const [newsletterData, setNewsletterData] = useState(null);
  const [newsletterSubject, setNewsletterSubject] = useState('');
  const [emailLists, setEmailLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState('');
  const [sendingNewsletter, setSendingNewsletter] = useState(false);
  
  // AI Caption inputs - initialize from Print Center property data if available
  const [beds, setBeds] = useState(initialPropertyData.beds || '');
  const [baths, setBaths] = useState(initialPropertyData.baths || '');
  const [sqft, setSqft] = useState(initialPropertyData.sqft || '');
  const [address, setAddress] = useState(initialPropertyData.address || '');
  const [price, setPrice] = useState(initialPropertyData.price || '');
  const [description, setDescription] = useState(initialPropertyData.description || '');
  const [propertyUrl, setPropertyUrl] = useState('');
  const [generatingCaption, setGeneratingCaption] = useState(false);
  
  // Caption style options
  const [postType, setPostType] = useState('new_listing');
  const [captionStyle, setCaptionStyle] = useState('minimalistic');
  const [captionOptions, setCaptionOptions] = useState([]);
  const [loadingAllCaptions, setLoadingAllCaptions] = useState(false);

  // Inline schedule sheet
  const [scheduleSheetOpen, setScheduleSheetOpen] = useState(false);
  const [scheduling, setScheduling] = useState(false);

  const handleInlineSchedule = async (scheduledIso, captionText) => {
    setScheduling(true);
    try {
      const campaignData = {
        template_id: templateId,
        type: template.type,
        content_data: buildContentData(),
        caption: captionText || '',
        scheduled_for: scheduledIso,
      };
      // Update existing draft if present (prevents duplicates when scheduling from a draft)
      if (draftCampaignId) {
        await apiClient.put(`/campaigns/${draftCampaignId}`, { ...campaignData, status: 'scheduled' });
      } else {
        await apiClient.post('/campaigns', campaignData);
      }
      toast.success('Campaign scheduled');
      setScheduleSheetOpen(false);
      navigate('/campaigns');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to schedule');
    } finally {
      setScheduling(false);
    }
  };

  useEffect(() => {
    fetchTemplate();
    fetchEmailLists();
    fetchAgentBrandContext();
  }, [templateId]);

  // Pull the agent's saved Brand Kit so {{agent_*}} / {{brokerage*}}
  // placeholders substitute even when the user opens a template directly
  // (no Magic Import). Fields from scraped property data still win because
  // brand context is merged UNDER `rawPropertyData` in effectivePropertyData.
  const fetchAgentBrandContext = async () => {
    try {
      const res = await apiClient.get('/profile/brand-kit');
      const bk = res.data || {};
      setAgentBrandContext({
        agent_name: bk.agent_name || '',
        agent_title: bk.agent_title || '',
        agent_phone: bk.agent_phone || '',
        agent_email: bk.agent_email || '',
        agent_website: bk.agent_website || '',
        agent_license: bk.agent_license || '',
        brokerage: bk.brokerage || '',
        brokerage_address: bk.brokerage_address || '',
        agent_headshot_url: bk.headshot_url || '',
        agent_logo_url: bk.logo_url || '',
      });
    } catch (_) {
      // Silent — placeholders just stay as raw tokens.
    }
  };

  const fetchEmailLists = async () => {
    try {
      const response = await apiClient.get('/email-lists');
      setEmailLists(response.data.lists || []);
    } catch (error) {
      console.error('Failed to fetch email lists');
    }
  };

  const fetchTemplate = async () => {
    try {
      const response = await apiClient.get(`/templates/${templateId}`);
      let templateData = response.data;
      
      // Auto-fill property data fields if coming from Print Center or RealProp Magic Import
      if ((isPrintTemplate || fromMagicCreate) && initialPropertyData && templateData.canvas_data?.elements) {
        templateData = {
          ...templateData,
          canvas_data: {
            ...templateData.canvas_data,
            elements: templateData.canvas_data.elements.map(el => substitutePropertyOnElement(el, initialPropertyData))
          }
        };
      }
      
      // Also handle multi-sided templates (postcards, brochures) - reuse the same mapping function
      if ((isPrintTemplate || fromMagicCreate) && initialPropertyData && templateData.canvas_data?.isMultiSided && templateData.canvas_data?.sides) {
        // Define the mapping function inline for multi-sided templates
        const mapMultiSideElement = (el) => {
          if (!el.text) return el;
          
          const elementId = el.id?.toLowerCase() || '';
          let newText = el.text;
          const hasPlaceholders = newText.includes('{{');
          
          const priceValue = initialPropertyData.price?.toString().replace(/[^0-9]/g, '') || '';
          const formattedPrice = priceValue ? `$${parseInt(priceValue).toLocaleString()}` : '';
          const priceWithoutDollar = priceValue ? parseInt(priceValue).toLocaleString() : '';
          const bedsValue = initialPropertyData.beds || '';
          const bathsValue = initialPropertyData.baths || '';
          const sqftValue = initialPropertyData.sqft?.toString().replace(/[^0-9]/g, '') || '';
          const addressValue = initialPropertyData.address || '';
          const summaryValue = initialPropertyData.property_summary || initialPropertyData.description || '';
          
          // Handle placeholders first
          if (hasPlaceholders) {
            newText = newText.replace(/\$\{\{price\}\}/g, formattedPrice || '${{price}}');
            newText = newText.replace(/\{\{price\}\}/g, priceWithoutDollar || '{{price}}');
            newText = newText.replace(/\{\{address\}\}/g, addressValue || '{{address}}');
            newText = newText.replace(/\{\{beds\}\}/g, bedsValue || '{{beds}}');
            newText = newText.replace(/\{\{baths\}\}/g, bathsValue || '{{baths}}');
            newText = newText.replace(/\{\{sqft\}\}/g, sqftValue || '{{sqft}}');
            newText = newText.replace(/\{\{summary\}\}/g, summaryValue || '');
            return { ...el, text: newText };
          }
          
          // ID-based replacement
          if ((el.isPropertyField || elementId === 'price' || elementId === 'price-label') && formattedPrice) {
            return { ...el, text: formattedPrice };
          }
          if ((elementId === 'address' || elementId === 'address-text') && addressValue) {
            return { ...el, text: addressValue };
          }
          if ((elementId === 'details' || elementId === 'features' || elementId === 'features-text')) {
            const parts = [];
            if (bedsValue) parts.push(`${bedsValue} Beds`);
            if (bathsValue) parts.push(`${bathsValue} Baths`);
            if (sqftValue) parts.push(`${parseInt(sqftValue).toLocaleString()} Sq Ft`);
            if (parts.length > 0) return { ...el, text: parts.join('  •  ') };
          }
          
          return el;
        };
        
        templateData = {
          ...templateData,
          canvas_data: {
            ...templateData.canvas_data,
            sides: {
              front: {
                ...templateData.canvas_data.sides.front,
                elements: (templateData.canvas_data.sides.front?.elements || []).map(mapMultiSideElement)
              },
              back: {
                ...templateData.canvas_data.sides.back,
                elements: (templateData.canvas_data.sides.back?.elements || []).map(mapMultiSideElement)
              }
            }
          }
        };
      }
      
      setTemplate(templateData);
    } catch (error) {
      console.error('Failed to load template:', error);
      toast.error('Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  // When opened with ?campaign=<id>, hydrate the editor with the existing draft's
  // canvas elements, caption, and property data so we update it on save/publish
  // instead of creating a duplicate.
  const fetchExistingCampaign = useCallback(async () => {
    if (!existingCampaignId) return;
    try {
      const res = await apiClient.get(`/campaigns/${existingCampaignId}`);
      const camp = res.data || {};
      if (camp.caption) setCaption(camp.caption);
      // Newsletter draft → hydrate subject + blocks so NewsletterBuilder
      // mounts with the saved state instead of an empty template.
      const nd = camp.content_data?.newsletter_data;
      if (nd && Array.isArray(nd.blocks)) {
        setNewsletterData(nd);
      }
      if (camp.content_data?.subject || camp.caption) {
        setNewsletterSubject(camp.content_data?.subject || camp.caption || '');
      }
      if (camp.content_data?.recipient_list_id) {
        setSelectedListId(camp.content_data.recipient_list_id);
      }
      const cd = camp.content_data?.canvas_data || camp.canvas_data;
      if (cd && Array.isArray(cd.elements) && cd.elements.length > 0) {
        // Belt-and-suspenders: re-run placeholder substitution on the saved
        // elements using the draft's own property_data. Most drafts created
        // through Magic Create already have substituted values, but legacy
        // drafts (created before substitution shipped) or drafts whose
        // property_data was updated later may still carry raw {{address}}/
        // {{price}} tokens. Also re-apply the hero image if no photoPlaceholder
        // has been filled yet.
        const pdForSub = camp.property_data || initialPropertyData || null;
        const heroFromDraft = pdForSub?.main_image_url
          || (Array.isArray(pdForSub?.photos) ? pdForSub.photos[0] : null);
        let restored = cd.elements;
        if (pdForSub) {
          restored = restored.map(el => substitutePropertyOnElement(el, pdForSub));
        }
        if (heroFromDraft) {
          restored = applyHeroImageToElements(restored, heroFromDraft);
        }
        // Stash these for the editor mount; setting draftElements is what
        // unblocks `draftHydrated=true` and renders UserTemplateEditor.
        setDraftElements(restored);
        setDraftBgColor(cd.backgroundColor || '#FFFFFF');
        setCustomElements(restored);
        if (cd.backgroundColor) setCustomBgColor(cd.backgroundColor);
      }
      // Hydrate property data fields if not already supplied via navigate state
      const pd = camp.property_data || {};
      if (pd && Object.keys(pd).length) {
        // Stash the full property_data so downstream consumers
        // (NewsletterBuilder, photo arrays, hero image) see it even when no
        // location.state.propertyData was passed on the Edit nav.
        setDraftPropertyData(pd);
        if (pd.address && !address) setAddress(pd.address);
        if (pd.price && !price) setPrice(pd.price);
        if (pd.beds && !beds) setBeds(pd.beds);
        if (pd.baths && !baths) setBaths(pd.baths);
        if (pd.sqft && !sqft) setSqft(pd.sqft);
        if ((pd.property_summary || pd.description) && !description) {
          setDescription(pd.description || pd.property_summary);
        }
      }
    } catch (err) {
      // Non-fatal — fall back to a fresh draft if the campaign was deleted
      console.warn('Failed to load existing campaign:', err);
    } finally {
      setDraftHydrated(true);
    }
  }, [existingCampaignId]);

  useEffect(() => {
    if (template && existingCampaignId) fetchExistingCampaign();
  }, [template, existingCampaignId, fetchExistingCampaign]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleElementsChange = (elements, bgColor) => {
    setCustomElements(elements);
    setCustomBgColor(bgColor);
  };

  // Auto-save debounced logic
  const performAutoSave = useCallback(async () => {
    if (!template) return;
    // Skip auto-save for reels/newsletters (they have their own save flows)
    if (template.type === 'reel' || template.type === 'newsletter') return;

    const contentData = {
      canvas_data: {
        width: template.canvas_data?.width || 1080,
        height: template.canvas_data?.height || 1080,
        backgroundColor: bgColorRef.current,
        elements: elementsRef.current
      }
    };

    setAutoSaveStatus('saving');
    try {
      if (draftCampaignId) {
        await apiClient.put(`/campaigns/${draftCampaignId}`, { content_data: contentData });
      } else {
        const res = await apiClient.post('/campaigns', {
          template_id: templateId,
          type: template.type,
          content_data: contentData,
          caption: ''
        });
        const newId = res.data?.campaign_id;
        if (newId) setDraftCampaignId(newId);
      }
      setAutoSaveStatus('saved');
    } catch (e) {
      console.error('Auto-save failed:', e);
      setAutoSaveStatus('error');
    }
  }, [template, templateId, draftCampaignId]);

  // Trigger auto-save on element changes (debounced 3s)
  useEffect(() => {
    if (!template || customElements.length === 0) return;
    if (template.type === 'reel' || template.type === 'newsletter') return;

    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      performAutoSave();
    }, 3000);

    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [customElements, customBgColor, performAutoSave, template]);

  // Capture preview image when switching to caption tab
  const handleTabChange = useCallback(async (newTab) => {
    if (newTab === 'caption' && activeTab === 'editor') {
      // Capture the canvas preview for post/story types
      if (template?.type !== 'reel' && template?.type !== 'newsletter' && editorRef.current) {
        try {
          // Use high resolution for print templates
          const isPrint = isPrintType(template?.type);
          const previewDataUrl = await editorRef.current.exportPreview(isPrint);
          if (previewDataUrl) {
            setPreviewImage(previewDataUrl);
          }
        } catch (error) {
          console.error('Failed to capture preview:', error);
          // Fallback to sync method
          const syncPreview = editorRef.current.getPreviewSync?.();
          if (syncPreview) {
            setPreviewImage(syncPreview);
          }
        }
      }
    }
    setActiveTab(newTab);
  }, [activeTab, template?.type, isPrintType]);

  // Callback to receive preview from editor
  const handlePreviewUpdate = useCallback((dataUrl) => {
    setPreviewImage(dataUrl);
  }, []);

  const generateCaption = async () => {
    setGeneratingCaption(true);
    try {
      const requestData = propertyUrl 
        ? { property_url: propertyUrl, post_type: postType, caption_style: captionStyle }
        : { 
            property_details: { beds, baths, sqft, address, price, description },
            post_type: postType,
            caption_style: captionStyle
          };
      
      const response = await apiClient.post('/ai/generate-caption', requestData);
      setCaption(response.data.caption);
      toast.success('Caption generated successfully!');
    } catch (error) {
      toast.error('Failed to generate caption');
    } finally {
      setGeneratingCaption(false);
    }
  };

  const generateAllCaptionStyles = async () => {
    setLoadingAllCaptions(true);
    setCaptionOptions([]);
    try {
      const requestData = propertyUrl 
        ? { property_url: propertyUrl, post_type: postType }
        : { 
            property_details: { beds, baths, sqft, address, price, description },
            post_type: postType
          };
      
      const response = await apiClient.post('/ai/generate-captions', requestData);
      setCaptionOptions(response.data.captions || []);
      toast.success('Generated 4 caption styles!');
    } catch (error) {
      toast.error('Failed to generate captions');
    } finally {
      setLoadingAllCaptions(false);
    }
  };

  const selectCaptionOption = (captionText) => {
    setCaption(captionText);
    toast.success('Caption selected!');
  };

  const buildContentData = () => {
    // Different content structure based on template type
    if (template?.type === 'reel') {
      return {
        reel_data: reelData || { slides: [], audio: null }
      };
    } else if (template?.type === 'newsletter') {
      return {
        newsletter_data: newsletterData || { blocks: [], html: '' }
      };
    } else {
      // Post or Story
      return {
        canvas_data: {
          width: template?.canvas_data?.width || 1080,
          height: template?.canvas_data?.height || 1080,
          backgroundColor: customBgColor,
          elements: customElements
        }
      };
    }
  };

  const handleReelSave = (data) => {
    setReelData(data);
    toast.success('Reel saved! Go to Caption & Schedule to publish.');
    setActiveTab('caption');
  };

  const handleNewsletterSave = (data) => {
    setNewsletterData(data);
    toast.success('Newsletter saved! Go to Send & Schedule.');
    setActiveTab('caption');
  };

  const sendNewsletterNow = async () => {
    if (!newsletterSubject.trim()) {
      toast.error('Please enter a subject line');
      return;
    }
    if (!selectedListId) {
      toast.error('Please select an email list');
      return;
    }

    setSendingNewsletter(true);
    try {
      // Save (or update existing draft) and then send
      const campaignData = {
        template_id: templateId,
        type: 'newsletter',
        content_data: buildContentData(),
        caption: newsletterSubject
      };

      let campaignId = draftCampaignId;
      if (campaignId) {
        await apiClient.put(`/campaigns/${campaignId}`, campaignData);
      } else {
        const campaignResponse = await apiClient.post('/campaigns', campaignData);
        campaignId = campaignResponse.data.campaign_id;
        if (campaignId) setDraftCampaignId(campaignId);
      }

      // Then send the newsletter
      await apiClient.post('/newsletters/send', {
        campaign_id: campaignId,
        recipient_list_id: selectedListId,
        subject: newsletterSubject
      });
      
      toast.success('Newsletter sent successfully!');
      navigate('/campaigns');
    } catch (error) {
      toast.error('Failed to send newsletter');
    } finally {
      setSendingNewsletter(false);
    }
  };

  const scheduleNewsletter = async () => {
    if (!newsletterSubject.trim()) {
      toast.error('Please enter a subject line');
      return;
    }
    if (!selectedListId) {
      toast.error('Please select an email list');
      return;
    }
    if (!scheduleDate || !scheduleTime) {
      toast.error('Please select a date and time');
      return;
    }

    setSaving(true);
    try {
      const scheduledFor = new Date(`${scheduleDate}T${scheduleTime}`).toISOString();
      
      const campaignData = {
        template_id: templateId,
        type: 'newsletter',
        content_data: {
          ...buildContentData(),
          recipient_list_id: selectedListId,
          subject: newsletterSubject
        },
        caption: newsletterSubject,
        scheduled_for: scheduledFor
      };
      
      if (draftCampaignId) {
        await apiClient.put(`/campaigns/${draftCampaignId}`, { ...campaignData, status: 'scheduled' });
      } else {
        await apiClient.post('/campaigns', campaignData);
      }
      toast.success('Newsletter scheduled successfully!');
      navigate('/campaigns');
    } catch (error) {
      toast.error('Failed to schedule newsletter');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const campaignData = {
        template_id: templateId,
        type: template.type,
        content_data: buildContentData(),
        caption: template?.type === 'newsletter' ? newsletterSubject : caption
      };
      
      if (draftCampaignId) {
        await apiClient.put(`/campaigns/${draftCampaignId}`, campaignData);
      } else {
        const res = await apiClient.post('/campaigns', campaignData);
        const newId = res.data?.campaign_id;
        if (newId) setDraftCampaignId(newId);
      }

      // Persist photo adjustments to listing if available
      if (editorRef.current && initialPropertyData?.listing_id) {
        try {
          const adjustments = editorRef.current.getPhotoAdjustments?.();
          if (adjustments && Object.keys(adjustments).length > 0) {
            await savePhotoAdjustments(adjustments);
          }
        } catch (e) { /* non-critical */ }
      }

      setAutoSaveStatus('saved');
      toast.success('Campaign saved as draft');
      navigate('/campaigns');
    } catch (error) {
      toast.error('Failed to save campaign');
    } finally {
      setSaving(false);
    }
  };

  const handleSchedule = async () => {
    if (!scheduleDate || !scheduleTime) {
      toast.error('Please select both date and time');
      return;
    }

    setSaving(true);
    try {
      const scheduledFor = new Date(`${scheduleDate}T${scheduleTime}`);

      const campaignData = {
        template_id: templateId,
        type: template.type,
        content_data: buildContentData(),
        caption,
        scheduled_for: scheduledFor.toISOString()
      };

      // Update existing draft if we have one; otherwise create a new campaign.
      if (draftCampaignId) {
        await apiClient.put(`/campaigns/${draftCampaignId}`, { ...campaignData, status: 'scheduled' });
      } else {
        await apiClient.post('/campaigns', campaignData);
      }
      toast.success('Campaign scheduled successfully');
      navigate('/campaigns');
    } catch (error) {
      toast.error('Failed to schedule campaign');
    } finally {
      setSaving(false);
    }
  };

  const handlePublishNow = async () => {
    // Validate at least one destination
    if (publishDestinations.length === 0) {
      toast.error('Pick at least one destination to publish to');
      return;
    }
    setSaving(true);
    try {
      const campaignData = {
        template_id: templateId,
        type: template.type,
        content_data: buildContentData(),
        caption
      };

      // Reuse the existing draft so we don't pile up duplicates.
      let cid = draftCampaignId;
      if (cid) {
        await apiClient.put(`/campaigns/${cid}`, campaignData);
      } else {
        const response = await apiClient.post('/campaigns', campaignData);
        cid = response.data.campaign_id;
        if (cid) setDraftCampaignId(cid);
      }

      // Export the rendered design to a public media URL Meta can fetch.
      // For Reels (video), we use the existing composition_id's exported MP4
      // (TODO: wire reel export pipeline). For images, export the Konva
      // stage to PNG, upload to /api/upload, and use the returned /api/files
      // URL — Meta Graph API requires an internet-reachable HTTPS URL.
      let mediaUrl = null;
      if (template.type === 'reel') {
        toast.error('Reel publishing to Meta is queued behind the export pipeline — coming next.');
        setSaving(false);
        return;
      }
      // Use the design snapshot captured when switching to the Caption tab.
      // The Konva editor lives inside the (now-inactive) Editor tab, so its
      // ref is unmounted here — fall back to a live export only if the stage
      // still happens to be mounted.
      let dataUrl = previewImage;
      if (!dataUrl && editorRef.current?.exportPreview) {
        try {
          dataUrl = await editorRef.current.exportPreview(true);
        } catch (exportErr) {
          console.error('Live export failed:', exportErr);
        }
      }
      if (dataUrl) {
        // Convert dataURL → Blob → FormData upload
        const blob = await (await fetch(dataUrl)).blob();
        const formData = new FormData();
        formData.append('file', blob, `campaign-${cid}.png`);
        const uploadResp = await apiClient.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const filesPath = uploadResp.data?.url;
        if (filesPath) {
          // /api/upload returns "/api/files/<id>" — make it absolute so
          // Meta can reach it. apiClient.defaults.baseURL ends in /api.
          const apiBase = (apiClient.defaults.baseURL || '').replace(/\/api\/?$/, '');
          mediaUrl = filesPath.startsWith('http')
            ? filesPath
            : `${apiBase}${filesPath}`;
        }
      }
      if (!mediaUrl) {
        toast.error('Could not export the design — try again or refresh the page.');
        setSaving(false);
        return;
      }

      // Fan out to all chosen destinations.
      const publishResp = await apiClient.post('/social/publish-multi', {
        destinations: publishDestinations,
        media_url: mediaUrl,
        caption,
        property_data: {
          address, price, beds, baths, sqft, description,
        },
        campaign_id: cid,
      });

      // Flip the campaign to "published" so it shows up in the published tab.
      try {
        await apiClient.post(`/campaigns/${cid}/publish`);
      } catch {
        // non-fatal — the publish_log rows already exist
      }

      const ok = publishResp.data?.success_count || 0;
      const total = publishResp.data?.total || publishDestinations.length;
      if (ok === total) {
        toast.success(`Published to ${ok} destination${ok > 1 ? 's' : ''}`);
      } else {
        toast(`${ok} of ${total} destinations published — check the publish log for details.`);
      }
      navigate('/campaigns');
    } catch (error) {
      const errorMessage = error.response?.data?.detail?.message || error.response?.data?.detail || 'Failed to publish campaign';
      toast.error(typeof errorMessage === 'string' ? errorMessage : 'Failed to publish campaign');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading template...</p>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col gap-4">
        <p className="text-muted-foreground">Template not found or could not be loaded.</p>
        <Button variant="outline" onClick={() => navigate('/campaigns')} data-testid="back-to-campaigns-btn">
          Back to Campaigns
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="noise-texture" />
      
      {/* Header */}
      <header className="sticky top-0 z-50 glassmorphic">
        <div className="container mx-auto px-3 sm:px-6 py-3 sm:py-4 flex justify-between items-center gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/templates')} data-testid="back-button" className="flex-shrink-0">
              <ArrowLeft className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <h1 className="text-base sm:text-xl font-bold truncate" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="create-campaign-logo">
              Create Campaign
            </h1>
          </div>
          <div className="flex items-center gap-1 sm:gap-4 flex-shrink-0">
            <span className="hidden md:inline text-sm text-muted-foreground">Welcome, {user?.name}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setScheduleSheetOpen(true)}
              data-testid="editor-schedule-button"
              className="flex-shrink-0"
              title="Schedule this post"
            >
              <Calendar className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Schedule</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-button">
              <LogOut className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Template Info Bar */}
      <div className="border-b border-border/50 bg-card/50">
        <div className="container mx-auto px-3 sm:px-6 py-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap min-w-0">
              <span className="text-sm font-medium">Template:</span>
              <span className="text-sm text-muted-foreground truncate max-w-[150px] sm:max-w-none">{template.name}</span>
              <span className="text-xs px-2 py-1 bg-secondary rounded-full capitalize">{template.type}</span>
              <span className="text-xs px-2 py-1 bg-secondary rounded-full capitalize">{template.category.replace('_', ' ')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="editor" data-testid="editor-tab">
              {template?.type === 'reel' ? <Video className="h-4 w-4 mr-2" /> : 
               template?.type === 'newsletter' ? <Mail className="h-4 w-4 mr-2" /> : 
               <Edit3 className="h-4 w-4 mr-2" />}
              {template?.type === 'reel' ? 'Video Editor' : 
               template?.type === 'newsletter' ? 'Newsletter Builder' : 
               'Design Editor'}
            </TabsTrigger>
            <TabsTrigger value="caption" data-testid="caption-tab">
              {isPrintType(template?.type) ? (
                <>
                  <Printer className="h-4 w-4 mr-2" />
                  Print & Export
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  {template?.type === 'newsletter' ? 'Subject & Schedule' : 'Caption & Schedule'}
                </>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Editor Tab - Renders different editors based on type */}
          <TabsContent value="editor" className="space-y-4">
            {/* Auto-save status indicator */}
            {template?.type !== 'reel' && template?.type !== 'newsletter' && (
              <div className="flex items-center justify-end gap-2 text-xs text-muted-foreground" data-testid="auto-save-status">
                {autoSaveStatus === 'saving' && (
                  <><Clock className="h-3 w-3 animate-spin" /> Saving...</>
                )}
                {autoSaveStatus === 'saved' && (
                  <><Save className="h-3 w-3 text-green-500" /> All changes saved</>
                )}
                {autoSaveStatus === 'error' && (
                  <span className="text-destructive">Auto-save failed</span>
                )}
              </div>
            )}
            <div className="min-h-[600px]">
              {template?.type === 'reel' ? (
                <ReelVideoEditor 
                  onSave={handleReelSave}
                  initialSlides={reelData?.slides || []}
                  initialAudio={reelData?.audio || null}
                />
              ) : template?.type === 'newsletter' ? (
                !draftHydrated ? (
                  <div className="flex items-center justify-center min-h-[400px]">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <NewsletterBuilder 
                    template={template}
                    onSave={handleNewsletterSave}
                    initialBlocks={newsletterData?.blocks || []}
                    availablePhotos={propertyPhotos}
                    propertyData={effectivePropertyData}
                  />
                )
              ) : !draftHydrated ? (
                <div className="flex items-center justify-center min-h-[400px]">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <UserTemplateEditor 
                  ref={editorRef}
                  template={template}
                  onElementsChange={handleElementsChange}
                  onPreviewUpdate={handlePreviewUpdate}
                  onNextStep={() => handleTabChange('caption')}
                  availablePhotos={propertyPhotos}
                  mainImageUrl={mainImageUrl}
                  photoAdjustments={activeListing?.photo_adjustments || initialPropertyData?.photo_adjustments || null}
                  onPhotoAdjustmentsChange={savePhotoAdjustments}
                  initialElements={draftElements || []}
                  initialBgColor={draftBgColor || '#FFFFFF'}
                />
              )}
            </div>
          </TabsContent>

          {/* Caption & Schedule Tab */}
          <TabsContent value="caption" className="space-y-6">
            {isPrintType(template?.type) ? (
              /* Print Templates: Simplified - main actions are in editor sidebar */
              <div className="max-w-2xl mx-auto space-y-6">
                {/* Preview */}
                <div className="border border-border/50 rounded-lg p-6 bg-card">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold">Your Design</h3>
                  </div>
                  <div className="aspect-[8.5/11] bg-secondary/20 rounded-lg flex items-center justify-center max-w-[400px] mx-auto overflow-hidden">
                    {previewImage ? (
                      <img 
                        src={previewImage} 
                        alt="Your customized design" 
                        className="w-full h-full object-contain rounded-lg" 
                        data-testid="print-preview-image"
                      />
                    ) : template?.thumbnail_url ? (
                      <img src={template.thumbnail_url} alt={template.name} className="w-full h-full object-cover rounded-lg" />
                    ) : (
                      <div className="text-center p-4">
                        <Image className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                        <p className="text-xs text-muted-foreground">Go back to Design Editor to see your design</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Print Specs & Save */}
                <div className="border border-border/50 rounded-lg p-6 bg-card">
                  <div className="grid grid-cols-2 gap-4 text-sm mb-6">
                    <div>
                      <p className="text-muted-foreground">Template</p>
                      <p className="font-medium capitalize">{template?.type} - {template?.name}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Print Size</p>
                      <p className="font-medium">
                        {template?.type === 'flyer' ? '8.5" × 11"' : 
                         template?.type === 'postcard' ? '4" × 6"' : 
                         template?.type === 'brochure' ? '11" × 8.5"' : 'Standard'}
                      </p>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={handleSaveDraft} 
                    disabled={saving}
                    className="w-full"
                    data-testid="save-campaign-button"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save to My Campaigns'}
                  </Button>
                  
                  <p className="text-xs text-muted-foreground mt-4 text-center">
                    Use the <strong>Download PNG</strong>, <strong>Export PDF</strong>, or <strong>Print Design</strong> buttons in the Design Editor sidebar for export options.
                  </p>
                </div>
              </div>
            ) : template?.type === 'newsletter' ? (
              /* Newsletter: Subject + Email Lists + Send/Schedule */
              <div className="max-w-2xl mx-auto space-y-6">
                {/* Subject Line */}
                <div className="border border-border/50 rounded-lg p-6 bg-card">
                  <div className="flex items-center gap-2 mb-4">
                    <Mail className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold">Email Subject</h3>
                  </div>
                  <Input
                    placeholder="Enter your newsletter subject line..."
                    value={newsletterSubject}
                    onChange={(e) => setNewsletterSubject(e.target.value)}
                    className="text-lg"
                    data-testid="newsletter-subject-input"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    A compelling subject line increases open rates
                  </p>
                </div>

                {/* Email List Selection */}
                <div className="border border-border/50 rounded-lg p-6 bg-card">
                  <div className="flex items-center gap-2 mb-4">
                    <Users className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold">Select Recipients</h3>
                  </div>
                  {emailLists.length === 0 ? (
                    <div className="text-center py-6">
                      <Users className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                      <p className="text-muted-foreground mb-3">No email lists yet</p>
                      <Button variant="outline" onClick={() => navigate('/profile')}>
                        Create Email List
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {emailLists.map((list) => (
                        <div
                          key={list.list_id}
                          className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-colors ${
                            selectedListId === list.list_id 
                              ? 'border-primary bg-primary/5' 
                              : 'border-border/50 hover:border-primary/50'
                          }`}
                          onClick={() => setSelectedListId(list.list_id)}
                          data-testid={`email-list-option-${list.list_id}`}
                        >
                          <div>
                            <p className="font-medium">{list.name}</p>
                            <p className="text-sm text-muted-foreground">{list.email_count || 0} subscribers</p>
                          </div>
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            selectedListId === list.list_id ? 'border-primary bg-primary' : 'border-muted-foreground'
                          }`}>
                            {selectedListId === list.list_id && (
                              <div className="w-2 h-2 bg-white rounded-full" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Schedule or Send Now */}
                <div className="border border-border/50 rounded-lg p-6 bg-card">
                  <div className="flex items-center gap-2 mb-4">
                    <Clock className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold">When to Send</h3>
                  </div>
                  
                  <div className="space-y-4">
                    {/* Send Now Button */}
                    <Button 
                      className="w-full" 
                      size="lg"
                      onClick={sendNewsletterNow}
                      disabled={sendingNewsletter || !newsletterSubject || !selectedListId}
                      data-testid="send-newsletter-now-button"
                    >
                      <Send className="h-5 w-5 mr-2" />
                      {sendingNewsletter ? 'Sending...' : 'Send Now'}
                    </Button>

                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-card px-2 text-muted-foreground">or schedule</span>
                      </div>
                    </div>

                    {/* Schedule Options */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="newsletter-date">Date</Label>
                        <Input
                          id="newsletter-date"
                          type="date"
                          value={scheduleDate}
                          onChange={(e) => setScheduleDate(e.target.value)}
                          className="mt-1"
                          data-testid="newsletter-schedule-date"
                        />
                      </div>
                      <div>
                        <Label htmlFor="newsletter-time">Time</Label>
                        <Input
                          id="newsletter-time"
                          type="time"
                          value={scheduleTime}
                          onChange={(e) => setScheduleTime(e.target.value)}
                          className="mt-1"
                          data-testid="newsletter-schedule-time"
                        />
                      </div>
                    </div>
                    
                    <Button 
                      variant="outline"
                      className="w-full" 
                      onClick={scheduleNewsletter}
                      disabled={saving || !newsletterSubject || !selectedListId || !scheduleDate || !scheduleTime}
                      data-testid="schedule-newsletter-button"
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      {saving ? 'Scheduling...' : 'Schedule Newsletter'}
                    </Button>
                  </div>
                </div>

                {/* Save Draft */}
                <div className="flex justify-center">
                  <Button variant="ghost" onClick={handleSaveDraft} disabled={saving} data-testid="save-newsletter-draft-button">
                    <Save className="h-4 w-4 mr-2" />
                    Save as Draft
                  </Button>
                </div>
              </div>
            ) : (
              /* Post/Reel/Story: Caption Generator + Schedule */
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* AI Caption Generator */}
                <div className="space-y-6">
                  <div className="border border-border/50 rounded-lg p-6 bg-card">
                    <div className="flex items-center gap-2 mb-4">
                      <Sparkles className="h-5 w-5 text-primary" />
                      <h3 className="font-semibold">AI Caption Generator</h3>
                    </div>

                    {/* Property Data Summary */}
                    {fromMagicCreate && (initialPropertyData.address || initialPropertyData.price) && (
                      <div className="flex items-center gap-2 text-xs bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-4" data-testid="caption-property-summary">
                        <span className="font-semibold text-emerald-700 whitespace-nowrap">Property loaded</span>
                        <span className="text-emerald-600 truncate">
                          {[
                            initialPropertyData.address,
                            initialPropertyData.price,
                            initialPropertyData.beds && `${initialPropertyData.beds}bd`,
                            initialPropertyData.baths && `${initialPropertyData.baths}ba`,
                            initialPropertyData.sqft && `${initialPropertyData.sqft}sqft`,
                          ].filter(Boolean).join(' · ')}
                        </span>
                      </div>
                    )}
                    
                    {/* Post Type Selection */}
                    <div className="mb-4">
                      <Label className="text-sm font-medium mb-2 block">Post Type</Label>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { value: 'new_listing', label: 'New Listing' },
                          { value: 'under_contract', label: 'Under Contract' },
                          { value: 'sold', label: 'Sold' },
                          { value: 'market_update', label: 'Market Update' },
                          { value: 'festive', label: 'Festive' }
                        ].map(type => (
                          <Button
                            key={type.value}
                            variant={postType === type.value ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setPostType(type.value)}
                            data-testid={`post-type-${type.value}`}
                          >
                            {type.label}
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Caption Style Selection */}
                    <div className="mb-4">
                      <Label className="text-sm font-medium mb-2 block">Caption Style</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { value: 'minimalistic', label: '✨ Minimalistic', desc: 'Short & clean' },
                          { value: 'storytelling', label: '📖 Story Telling', desc: 'Engaging narrative' },
                          { value: 'lifestyle', label: '🏡 Lifestyle', desc: 'Aspirational' },
                          { value: 'witty', label: '😄 Witty & Casual', desc: 'Fun & memorable' }
                        ].map(style => (
                          <Button
                            key={style.value}
                            variant={captionStyle === style.value ? 'default' : 'outline'}
                            size="sm"
                            className="flex-col h-auto py-2"
                            onClick={() => setCaptionStyle(style.value)}
                            data-testid={`caption-style-${style.value}`}
                          >
                            <span>{style.label}</span>
                            <span className="text-xs opacity-70">{style.desc}</span>
                          </Button>
                        ))}
                      </div>
                    </div>
                    
                    <Tabs defaultValue="details">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="details">Property Details</TabsTrigger>
                        <TabsTrigger value="url">Property URL</TabsTrigger>
                      </TabsList>

                      <TabsContent value="details" className="space-y-3 mt-4">
                        <div className="grid grid-cols-3 gap-2">
                          <Input placeholder="Beds" value={beds} onChange={(e) => setBeds(e.target.value)} data-testid="beds-input" />
                          <Input placeholder="Baths" value={baths} onChange={(e) => setBaths(e.target.value)} data-testid="baths-input" />
                          <Input placeholder="SqFt" value={sqft} onChange={(e) => setSqft(e.target.value)} data-testid="sqft-input" />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Input placeholder="Address" value={address} onChange={(e) => setAddress(e.target.value)} data-testid="address-input" />
                          <Input placeholder="Price" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="price-input" />
                        </div>
                        <Textarea
                          placeholder="Property description, unique features, neighborhood..."
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          rows={3}
                          data-testid="description-input"
                        />
                        <div className="flex gap-2">
                          <Button onClick={generateCaption} disabled={generatingCaption} className="flex-1" data-testid="generate-caption-button">
                            {generatingCaption ? 'Generating...' : 'Generate Caption'}
                          </Button>
                          <Button variant="outline" onClick={generateAllCaptionStyles} disabled={loadingAllCaptions} data-testid="generate-all-captions-button">
                            {loadingAllCaptions ? '...' : '4 Styles'}
                          </Button>
                        </div>
                      </TabsContent>

                      <TabsContent value="url" className="space-y-3 mt-4">
                        <Input
                          placeholder="https://www.zillow.com/..."
                          value={propertyUrl}
                          onChange={(e) => setPropertyUrl(e.target.value)}
                          data-testid="property-url-input"
                        />
                        <p className="text-xs text-muted-foreground">Note: Some sites may block automated access</p>
                        <div className="flex gap-2">
                          <Button onClick={generateCaption} disabled={generatingCaption} className="flex-1" data-testid="generate-url-caption-button">
                            {generatingCaption ? 'Generating...' : 'Generate Caption'}
                          </Button>
                          <Button variant="outline" onClick={generateAllCaptionStyles} disabled={loadingAllCaptions} data-testid="generate-all-url-captions-button">
                            {loadingAllCaptions ? '...' : '4 Styles'}
                          </Button>
                        </div>
                      </TabsContent>
                    </Tabs>
                  </div>

                  {/* Generated Caption Options */}
                  {captionOptions.length > 0 && (
                    <div className="border border-border/50 rounded-lg p-6 bg-card">
                      <h3 className="font-semibold mb-4">Choose a Caption Style</h3>
                      <div className="space-y-3">
                        {captionOptions.map((opt, idx) => (
                          <div key={idx} className="border border-border/30 rounded-lg p-3 hover:border-primary/50 cursor-pointer transition-colors" onClick={() => selectCaptionOption(opt.caption)}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium capitalize">{opt.style.replace('_', ' ')}</span>
                              <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); selectCaptionOption(opt.caption); }}>
                                Use This
                              </Button>
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-3">{opt.caption}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Caption */}
                  <div className="border border-border/50 rounded-lg p-6 bg-card">
                    <Label htmlFor="caption" className="text-base font-semibold">Caption</Label>
                    <Textarea
                      id="caption"
                      value={caption}
                      onChange={(e) => setCaption(e.target.value)}
                      rows={8}
                      placeholder="Write or generate your caption here..."
                      className="mt-3"
                      data-testid="caption-textarea"
                    />
                  </div>
                </div>

              {/* Schedule & Actions */}
              <div className="space-y-6">
                {/* Preview */}
                <div className="border border-border/50 rounded-lg p-6 bg-card">
                  <h3 className="font-semibold mb-4">Preview</h3>
                  <div className="aspect-square bg-secondary/20 rounded-lg flex items-center justify-center max-w-[300px] mx-auto overflow-hidden">
                    {previewImage ? (
                      <img 
                        src={previewImage} 
                        alt="Your customized design" 
                        className="w-full h-full object-contain rounded-lg" 
                        data-testid="design-preview-image"
                      />
                    ) : template?.thumbnail_url ? (
                      <img src={template.thumbnail_url} alt={template.name} className="w-full h-full object-cover rounded-lg" />
                    ) : (
                      <div className="text-center p-4">
                        <Image className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                        <p className="text-xs text-muted-foreground">Go back to Design Editor to customize your template</p>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-center text-muted-foreground mt-3">
                    {previewImage ? 'Your customized design' : 'Your customized design will appear here'}
                  </p>
                </div>

                {template?.type === 'reel' ? (
                  /* Reels are video and must be rendered before publishing — funnel to the Reel Maker */
                  <div className="border border-border/50 rounded-lg p-6 bg-card" data-testid="reel-publish-redirect">
                    <div className="flex items-center gap-2 mb-3">
                      <Video className="h-5 w-5 text-primary" />
                      <h3 className="font-semibold">Publishing a Reel</h3>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      Reels are videos and need to be rendered before they can go out. Open this in the Reel Maker to render your reel, then publish or schedule it to Instagram &amp; Facebook — your caption is generated there automatically.
                    </p>
                    <Button
                      onClick={() => navigate('/reel-editor', { state: { propertyData: initialPropertyData || { address, price, beds, baths, sqft, description } } })}
                      className="w-full"
                      data-testid="open-reel-maker-button"
                    >
                      <Video className="h-4 w-4 mr-2" />
                      Open in Reel Maker
                    </Button>
                    <Button variant="outline" onClick={handleSaveDraft} disabled={saving} className="w-full mt-3" data-testid="save-draft-button">
                      <Save className="h-4 w-4 mr-2" />
                      Save as Draft
                    </Button>
                  </div>
                ) : (
                <>
                {/* Schedule */}
                <div className="border border-border/50 rounded-lg p-6 bg-card">
                  <div className="flex items-center gap-2 mb-4">
                    <Calendar className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold">Schedule (Optional)</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="date">Date</Label>
                      <Input
                        id="date"
                        type="date"
                        value={scheduleDate}
                        onChange={(e) => setScheduleDate(e.target.value)}
                        className="mt-1"
                        data-testid="schedule-date-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="time">Time</Label>
                      <Input
                        id="time"
                        type="time"
                        value={scheduleTime}
                        onChange={(e) => setScheduleTime(e.target.value)}
                        className="mt-1"
                        data-testid="schedule-time-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Publish Destinations */}
                <div className="border border-border/50 rounded-lg p-6 bg-card" data-testid="publish-destinations-section">
                  <PublishDestinationPicker
                    campaignType={template?.type}
                    value={publishDestinations}
                    onChange={setPublishDestinations}
                  />
                </div>

                {/* Actions */}
                <div className="border border-border/50 rounded-lg p-6 bg-card">
                  <h3 className="font-semibold mb-4">Actions</h3>
                  <div className="space-y-3">
                    <Button variant="outline" onClick={handleSaveDraft} disabled={saving} className="w-full" data-testid="save-draft-button">
                      <Save className="h-4 w-4 mr-2" />
                      Save as Draft
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={handleSchedule} 
                      disabled={saving || !scheduleDate || !scheduleTime} 
                      className="w-full"
                      data-testid="schedule-button"
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      Schedule for Later
                    </Button>
                    <Button onClick={handlePublishNow} disabled={saving} className="w-full" data-testid="publish-button">
                      <Send className="h-4 w-4 mr-2" />
                      Publish Now
                    </Button>
                  </div>
                </div>
                </>
                )}
              </div>
            </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <ScheduleSheet
        open={scheduleSheetOpen}
        onOpenChange={setScheduleSheetOpen}
        caption={caption}
        setCaption={setCaption}
        postType={postType}
        propertyDetails={{ beds, baths, sqft, address, price, description }}
        onSchedule={handleInlineSchedule}
        loading={scheduling}
      />
    </div>
  );
};
